import { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Stack,
  Chip,
  InputAdornment,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import Iconify from '../../../components/iconify';
import { getInternBalances } from '../../../services/leaveService';

// Format numeric values as floating point (e.g. 1.00); fall back to em dash when empty.
const fmt = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(2));
// Shared style so every numeric chip has the same width and its border lines up evenly.
const numberChipSx = { minWidth: 72 };

// Render a date as a short readable string; guard against missing/invalid values.
const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// Build a short list of recent years for the year selector (current year and the four prior).
const buildYearOptions = () => {
  const current = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, i) => current - i);
};

export default function InternLeaveBalances() {
  const yearOptions = buildYearOptions();
  const [year, setYear] = useState(yearOptions[0]);
  const [history, setHistory] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const searchTimerRef = useRef(null);

  const { data: response = { rows: [], count: 0 }, isLoading } = useQuery({
    queryKey: ['internBalances', year, history, searchQuery, page, rowsPerPage],
    queryFn: async () => {
      try {
        const res = await getInternBalances(year, {
          search: searchQuery,
          page: page + 1,
          pageSize: rowsPerPage,
          history,
        });
        return res?.data?.data || { rows: [], count: 0 };
      } catch {
        return { rows: [], count: 0 };
      }
    },
    keepPreviousData: true,
  });

  const balances = response.rows || [];
  const totalCount = response.count || 0;

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSearchQuery(value);
      setPage(0);
    }, 400);
  };

  const handleYearChange = (e) => {
    setYear(Number(e.target.value));
    setPage(0);
  };

  const handleHistoryToggle = (e) => {
    setHistory(e.target.checked);
    setPage(0);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h6">Intern Leave Balances — {year}</Typography>
          <Typography variant="body2" color="text.secondary">
            Interns accrue 1 leave per month. &quot;Available&quot; is the unlocked pool (earned + privileged −
            extra); &quot;Leaves Taken&quot; is shown separately.
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControlLabel
            control={<Switch checked={history} onChange={handleHistoryToggle} />}
            label="Show promoted / past interns"
          />
          <TextField select size="small" label="Year" value={year} onChange={handleYearChange} sx={{ width: 120 }}>
            {yearOptions.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            placeholder="Search by name or email…"
            value={searchInput}
            onChange={handleSearchChange}
            sx={{ width: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" width={18} />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Stack>

      {isLoading ? (
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      ) : totalCount === 0 && !searchQuery ? (
        <Alert severity="info">
          {history ? `No interns found for ${year}.` : `No active interns found for ${year}.`}
        </Alert>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Intern</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell align="center">Months Unlocked</TableCell>
                  <TableCell align="center">Earned</TableCell>
                  <TableCell align="center">Privileged</TableCell>
                  <TableCell align="center">Extra</TableCell>
                  <TableCell align="center">Available</TableCell>
                  <TableCell align="center">Leaves Taken</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {balances.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      No results found for &quot;{searchQuery}&quot;
                    </TableCell>
                  </TableRow>
                ) : (
                  balances.map((row) => {
                    const isPromoted = Boolean(row.fullTimeFrom) || row.employmentType === 'full_time';
                    const isInactive = row.status && row.status !== 'active';
                    return (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <span>{row.userName}</span>
                            {isPromoted && (
                              <Tooltip
                                title={row.fullTimeFrom ? `Promoted to full-time on ${formatDate(row.fullTimeFrom)}` : 'Promoted to full-time'}
                              >
                                <Chip
                                  label="Promoted"
                                  size="small"
                                  sx={{ fontWeight: 600, bgcolor: '#D0E8FF', color: '#1877F2', border: 'none' }}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={isInactive ? 'Inactive' : 'Active'}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              bgcolor: isInactive ? '#FDECEA' : '#C8F5DC',
                              color: isInactive ? '#D32F2F' : '#0A8043',
                              border: 'none',
                            }}
                          />
                        </TableCell>
                        <TableCell>{formatDate(row.joiningDate)}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${row.monthsUnlocked ?? 0} mo`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={numberChipSx}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={fmt(row.earnedLeaves)} size="small" color="primary" variant="outlined" sx={numberChipSx} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={fmt(row.privilegedLeaves)} size="small" color="success" variant="outlined" sx={numberChipSx} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={row.extraLeaves > 0 ? fmt(row.extraLeaves) : '—'}
                            size="small"
                            color={row.extraLeaves > 0 ? 'error' : 'default'}
                            variant={row.extraLeaves > 0 ? 'filled' : 'outlined'}
                            sx={numberChipSx}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <strong>{fmt(row.availableBalance)}</strong>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={fmt(row.leavesTaken)} size="small" color="warning" variant="outlined" sx={numberChipSx} />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      )}
    </Box>
  );
}
